import React, { CSSProperties, useContext } from "react";
import { CustomType, customTypeConstants, minimalCustomType } from "../../../api/CustomTypes/CustomTypes";
import {
  EntityConstants,
  GenericEntity,
  ICustomTypedEntity,
  IUniqueEntity,
  SortDirection,
} from "../../../api/GenericTypes";
// import { KeyValuePair } from "../../../common/detailviews/MetadataFormatting";
// import { NotSet } from "../../../common/misc/UIconstants";
import { Table } from "../../../common/panels/Detail/DetailTable";
import { CustomFieldRenderer } from "../../CustomFields/CustomFieldRenderer";
import { useCustomType } from "./useCustomTypes";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import {
  GenericVirtualizedTableCell,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { NotSet } from "../../../common/misc/UIconstants";
import { CustomTypeTemplate } from "../../../api/CustomTypes/CustomTypeTemplates";
import { EntityDetailTableProps } from "../../../common/entity/EntityInterfaces";
import { GenericEntityConstantsEntities, getEntityConstants } from "../../../api/GenericConstants";
import { IconNames, LucideIcon } from "../../../common/icon/LucideIcon";
import { PrintCompatibleLink } from "../../../common/misc/PrintCompatibleLink.tsx/PrintCompatibleLink";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { getDetailLink } from "../../../main/Routing";

// ################# Basic render fns
/**
 * Renders a basic custom type.
 * @author @CorradoSurmanowicz
 * @param entityType - The type of the entity.
 * @returns The rendered custom type.
 */
interface CustomTypeBasicRendererProps {
  entityType?: string;
  style?: CSSProperties;
}
export const CustomTypeBasicRenderer = ({ entityType, style }: CustomTypeBasicRendererProps) => {
  return (
    <label className="label label-soft-secondary" style={{ margin: 0, lineHeight: 1.2, ...style }}>
      {minimalCustomType}
    </label>
  );
  // return (
  //   <div className={"flex row-nowrap align-center gap-5"} style={style}>
  //     {minimalCustomType} {entityType}
  //   </div>
  // );
};

interface CustomTypeLabelRendererProps<Entity extends Partial<ICustomTypedEntity>> {
  entity: Entity;
  propertyOverride?: string;
  iconOverride?: IconNames;
  style?: React.CSSProperties;
}
/**
 * Renders a label for a custom-typed entity, displaying an icon and the custom type's name.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity, extending Partial<ICustomTypedEntity>.
 * @param props - The properties for the label renderer.
 * @param props.entity - The entity object, which may have a customType.
 * @param props.propertyOverride - Optional property to override the displayed name.
 * @param props.iconOverride - Optional icon to override the displayed icon.
 * @param props.style - Optional CSS properties to apply to the label.
 * @returns A JSX element rendering the custom type label, or a basic renderer if no customType is present.
 */
export const CustomTypeLabelRenderer = <Entity extends Partial<ICustomTypedEntity>>({
  entity,
  propertyOverride,
  iconOverride,
  style,
}: CustomTypeLabelRendererProps<Entity>) => {
  if (!entity.customType) return <CustomTypeBasicRenderer style={style} />;
  return (
    <label className="label label-soft-secondary" style={{ margin: 0, ...style }}>
      <LucideIcon name={iconOverride ?? customTypeConstants.icon} color={"var(--primary)"} style={{ flexShrink: 0 }} />{" "}
      {propertyOverride ?? entity.customType.name}
    </label>
  );
};

/**
 * Renders a custom type for detail pages for a given entity.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @param {CustomTypeDetailRowProps<Entity>} props - The component props.
 * @returns {JSX.Element} - The rendered custom type section.
 */

interface CustomTypeDetailRowProps<
  EntityTypeId extends GenericEntityConstantsEntities,
  Entity extends ICustomTypedEntity
> {
  entity: Entity;
  entityTypeId: EntityTypeId;
  showLinks?: EntityDetailTableProps<any>["showLinks"];
}

export const CustomTypeDetailRow = <
  EntityTypeId extends GenericEntityConstantsEntities,
  Entity extends ICustomTypedEntity
>({
  entity,
  entityTypeId,
  showLinks = true,
}: CustomTypeDetailRowProps<EntityTypeId, Entity>) => {
  // const entityConstants = getEntityConstants(entityTypeId).entityConstants;

  return (
    <Table.Body.RowContent
      title={`Custom type`}
      content={<CustomTypePropertyRenderer entityTypeId={entityTypeId} entity={entity} disableLink={!showLinks} />}
    />
  );
};

interface CustomTypePropertyRendererProps<
  EntityTypeId extends GenericEntityConstantsEntities,
  Entity extends Partial<ICustomTypedEntity>
> {
  entityTypeId: EntityTypeId;
  entity: Entity;
  disableLink?: boolean;
}
/**
 * Renders the custom type property of an entity, displaying its name or a minimal label,
 * and optionally as a link depending on the `disableLink` prop.
 * @author @CorradoSurmanowicz
 * @template EntityTypeId - The type of the entity type identifier, extending `GenericEntityConstantsEntities`.
 * @template Entity - The type of the entity, extending `Partial<ICustomTypedEntity>`.
 * @param props - The properties for the renderer.
 * @param props.entityTypeId - The identifier for the entity type.
 * @param props.entity - The entity object, possibly containing a custom type.
 * @param props.disableLink - If true, disables rendering the custom type as a link.
 * @returns A React element displaying the custom type property, a minimal label if not present, or a link if enabled.
 */
export const CustomTypePropertyRenderer = <
  EntityTypeId extends GenericEntityConstantsEntities,
  Entity extends Partial<ICustomTypedEntity>
>({
  entityTypeId,
  entity,
  disableLink,
}: CustomTypePropertyRendererProps<EntityTypeId, Entity>) => {
  const { route } = useContext(SessionContext);
  const entityConstants = getEntityConstants(entityTypeId).entityConstants;
  if (!entity.customType)
    return (
      <label className="label label-soft-secondary" style={{ margin: 0 }}>
        {minimalCustomType}
      </label>
    );
  // if (!entity.customType)
  //   return (
  //     <div className={"container_label_name"}>
  //       <span style={{ color: "var(--gray-400)", fontWeight: 500 }}>
  //         {minimalCustomType} {entityConstants.entitySingular}
  //       </span>
  //     </div>
  //   );

  if (disableLink)
    return (
      <label className="label label-soft-secondary" style={{ margin: 0 }}>
        {entity.customType.name}
      </label>
    );

  // return <LinkEntity entityConstants={customTypeConstants} property={entity.customType} />;
  return (
    <PrintCompatibleLink
      to={route(getDetailLink(customTypeConstants.frontendIndexRoute, entity.customType.id))}
      className={"flex row-nowrap align-center gap-5"}
      style={{ display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
      showLink
    >
      <CustomTypeLabelRenderer entity={entity} style={{ cursor: "pointer" }} iconOverride={entityConstants.icon} />
    </PrintCompatibleLink>
  );
};

export const renderCustomTypeColumn = <Entity extends GenericEntity & ICustomTypedEntity, SortByOptions extends string>(
  entityConstants: EntityConstants,
  sort: SortState<SortByOptions>[],
  setSort: (newSort: SortState<SortByOptions>[]) => Promise<void>
): GenericVirtualizedTableCell<Entity> => {
  return {
    id: "default-customType",
    Header: "Custom type",
    accessor: (row) => (
      <CustomTypeLabelRenderer entity={row} style={{ cursor: "pointer" }} iconOverride={entityConstants.icon} />
    ),
    width: 200,
    align: "left",
    clearSort: async (id: string) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        const newSort = [...sort];
        newSort.splice(currentSortIndex, 1);
        await setSort(newSort);
      }
    },
    sortingFn: async (id, allowMultiSort) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (allowMultiSort) {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "DESC",
              property: "CUSTOM_TYPE" as SortByOptions,
            };
            await setSort(newSort);
          } else {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "ASC",
              property: "CUSTOM_TYPE" as SortByOptions,
            };
            await setSort(newSort);
          }
        } else {
          await setSort([
            ...sort,
            {
              headerId: id,
              sortDirection: "ASC",
              property: "CUSTOM_TYPE" as SortByOptions,
            },
          ]);
        }
      } else {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            await setSort([
              {
                headerId: id,
                sortDirection: "DESC" as SortDirection,
                property: "CUSTOM_TYPE" as SortByOptions,
              },
            ]);
          } else {
            await setSort([
              {
                headerId: id,
                sortDirection: "ASC" as SortDirection,
                property: "CUSTOM_TYPE" as SortByOptions,
              },
            ]);
          }
        } else {
          await setSort([
            {
              headerId: id,
              sortDirection: "ASC",
              property: "CUSTOM_TYPE" as SortByOptions,
            },
          ]);
        }
      }
    },
    sortDirection: (id) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        return sort[currentSortIndex].sortDirection;
      }
      return undefined;
    },
    sortIndex: (id: string) => {
      if (Array.isArray(sort) && !!sort.length) {
        const currentSortIndex = sort.findIndex((s) => s.headerId === id);
        return currentSortIndex >= 0 ? currentSortIndex + 1 : undefined;
      }
      return undefined;
    },
  } as GenericVirtualizedTableCell<Entity>;
};

/**
 * Renders a custom type for a given entity.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @param {CustomTypeRendererProps<Entity>} props - The component props.
 * @returns {JSX.Element} - The rendered custom type section.
 */
interface CustomTypeRendererProps<Entity extends ICustomTypedEntity & IUniqueEntity> {
  entity: Entity;
  rowStyle?: CSSProperties;
  tableStyle?: CSSProperties;
  includeCustomFieldId?: boolean;
  typeOverride?: CustomTypeTemplate;
  showLinks?: EntityDetailTableProps<any>["showLinks"];
}

export const CustomTypeRenderer = <Entity extends ICustomTypedEntity & IUniqueEntity>({
  entity,
  rowStyle,
  tableStyle,
  includeCustomFieldId = false,
  showLinks,
}: CustomTypeRendererProps<Entity>) => {
  const { type, status, fetchStatus, error } = useCustomType({
    typeId: entity.customType?.id,
    enabled: !!entity.customType?.id,
  });
  const currentType =
    entity.customType &&
    Object.hasOwn(entity.customType, "isTemplate") &&
    !!(entity.customType as CustomType & { isTemplate?: boolean }).isTemplate
      ? (entity.customType as CustomType)
      : type;
  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
      {currentType ? (
        <CustomTypeSectionRenderer
          entity={entity}
          customType={currentType}
          rowStyle={rowStyle}
          includeCustomFieldId={includeCustomFieldId}
          tableStyle={tableStyle}
          showLinks={showLinks}
        />
      ) : (
        <></>
      )}
    </LoadingWrapper>
  );
};

/**
 * Renders a custom type section.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @param {CustomTypeSectionRendererProps<Entity>} props - The component props.
 * @returns {JSX.Element} - The rendered custom type section.
 */
interface CustomTypeSectionRendererProps<Entity extends ICustomTypedEntity & IUniqueEntity>
  extends CustomTypeRendererProps<Entity> {
  customType: CustomType;
  showLinks?: EntityDetailTableProps<any>["showLinks"];
}

const CustomTypeSectionRenderer = <Entity extends ICustomTypedEntity & IUniqueEntity>({
  entity,
  customType,
  rowStyle,
  tableStyle,
  includeCustomFieldId,
  showLinks,
}: CustomTypeSectionRendererProps<Entity>) => {
  if (!customType) return <></>;
  return (
    <>
      {customType.sections?.map((section, index) => {
        return (
          <Table
            key={`Section-${index}`}
            isFolded={section.isFolded}
            noPadding
            style={{
              ...(!!section.isHeadless ? { margin: 0, padding: 0 } : {}),
              ...tableStyle,
            }}
          >
            {!section.isHeadless && <Table.Head>{section.name ?? NotSet}</Table.Head>}
            <Table.Body>
              {section.customFields?.map((customField, indexChild) => {
                return (
                  <CustomFieldRenderer
                    title={customField.name}
                    value={entity.customFields?.[customField.id]}
                    customfield={customField}
                    key={indexChild}
                    rowStyle={rowStyle}
                    includeCustomFieldId={includeCustomFieldId}
                    showLinks={showLinks}
                  />
                );
              })}
            </Table.Body>
          </Table>
        );
      })}
    </>
  );
};

interface CustomTypeMinimalRendererProps<Entity extends ICustomTypedEntity & IUniqueEntity> {
  entity: Entity;
  rowStyle?: CSSProperties;
}

/**
 * Renders a minimal view of a custom type entity.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity that extends `ICustomTypedEntity` and `IUniqueEntity`.
 * @param {CustomTypeMinimalRendererProps<Entity>} props - The properties for the renderer.
 * @param {Entity} props.entity - The entity to be rendered.
 * @param {React.CSSProperties} [props.rowStyle] - Optional style to be applied to each row.
 * @returns {JSX.Element} The rendered minimal view of the custom type entity.
 */
export const CustomTypeMinimalRenderer = <Entity extends ICustomTypedEntity & IUniqueEntity>({
  entity,
  rowStyle,
}: CustomTypeMinimalRendererProps<Entity>) => {
  return (
    <>
      {entity.customValues?.map((section, index) => (
        <Table key={`Section-${index}`} noPadding>
          <Table.Head>{section.name ?? ""}</Table.Head>
          <Table.Body>
            {section.content?.map((customField, indexChild) => {
              return (
                <CustomFieldRenderer
                  title={customField.name}
                  value={customField.value}
                  customfield={customField}
                  key={indexChild}
                  rowStyle={rowStyle}
                  includeCustomFieldId={false}
                  showLinks={false}
                />
              );
            })}
          </Table.Body>
        </Table>
      ))}
    </>
  );
};
